import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api, type ProvisionalReviewDetail } from '../../api.ts';
import {
  Badge,
  Button,
  ErrorBox,
  Field,
  Loading,
  PageHeader,
  Panel,
  Textarea,
} from '../../components/ui.tsx';
import { useFormat } from '../purchasing/common.tsx';

const STATUS_TONE = { pending: 'info', confirmed: 'ok', cadastre_error: 'off' } as const;

/** Resposta do gestor ao título provisório no Master: só contrato, valor e competência (ADR 0094). */
export function ProvisionalReviewPage() {
  const { t } = useTranslation();
  const fmt = useFormat();
  const { id } = useParams({ strict: false }) as { id: string };
  const queryClient = useQueryClient();
  const [note, setNote] = useState('');
  const key = ['provisional-review', id];
  const data = useQuery({
    queryKey: key,
    queryFn: () => api.get<ProvisionalReviewDetail>(`/finance/provisional-reviews/${id}`),
  });
  const answer = useMutation({
    mutationFn: (value: 'confirmed' | 'cadastre_error') =>
      api.post(`/finance/provisional-reviews/${id}/answer`, {
        answer: value,
        note: note.trim() || undefined,
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: key }),
  });
  const d = data.data;
  const item = (label: string, value: React.ReactNode) => (
    <div>
      <dt className="text-muted">{label}</dt>
      <dd>{value || '—'}</dd>
    </div>
  );
  return (
    <>
      <PageHeader
        title={t('finance.provisionalReview.title')}
        description={t('finance.provisionalReview.about')}
      />
      {data.isPending && <Loading />}
      {data.isError && <ErrorBox error={data.error} />}
      {d && (
        <Panel>
          <div className="flex flex-col gap-6">
            <div className="flex flex-wrap items-center gap-2">
              <Badge tone={STATUS_TONE[d.status]}>
                {t(`finance.provisionalReview.statuses.${d.status}`)}
              </Badge>
              <span className="text-sm text-muted">
                {t(`finance.provisionalReview.reasons.${d.reason}`)}
              </span>
            </div>
            <dl className="grid grid-cols-1 gap-x-6 gap-y-2 text-sm sm:grid-cols-3">
              {item(t('finance.provisionalReview.fields.payable'), d.payableCode)}
              {item(
                t('finance.provisionalReview.fields.contract'),
                `${d.contractCode} · ${d.contractTitle}`,
              )}
              {item(t('finance.provisionalReview.fields.partner'), d.partnerName)}
              {item(t('finance.provisionalReview.fields.competence'), d.competence)}
              {item(
                t('finance.provisionalReview.fields.amount'),
                fmt.money(d.grossAmount, d.currency),
              )}
              {item(t('finance.provisionalReview.fields.dueDate'), fmt.date(d.firstDueDate))}
              {item(t('finance.provisionalReview.fields.firstDeadline'), fmt.date(d.firstDeadline))}
              {item(t('finance.provisionalReview.fields.finalDeadline'), fmt.date(d.finalDeadline))}
            </dl>
            {d.answerNote && <p className="text-sm">{d.answerNote}</p>}
            {d.supplierNoticeAt && (
              <p className="text-sm text-muted">{t('finance.provisionalReview.supplierNotice')}</p>
            )}
            {d.canAnswer && (
              <div className="flex flex-col gap-3">
                <ErrorBox error={answer.error} showPaths={false} />
                <Field
                  label={t('finance.provisionalReview.fields.note')}
                  hint={t('finance.provisionalReview.noteHint')}
                >
                  <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} />
                </Field>
                <div className="flex flex-wrap justify-end gap-2">
                  <Button
                    variant="secondary"
                    disabled={answer.isPending}
                    onClick={() => answer.mutate('cadastre_error')}
                  >
                    {t('finance.provisionalReview.reportError')}
                  </Button>
                  <Button disabled={answer.isPending} onClick={() => answer.mutate('confirmed')}>
                    {t('finance.provisionalReview.confirm')}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </Panel>
      )}
    </>
  );
}
